import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Wrench } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [name, setName] = useState("");

  const load = useCallback(() => api.get("/teams").then((r) => setTeams(r.data)).catch((e) => toast.error(apiErr(e))), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/teams", { name: name.trim() }); setName(""); load(); toast.success("Tým přidán"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2540]">Pracovní týmy</h1>
        <p className="text-slate-500 mt-1">Týmy jsou společné pro celou aplikaci. Jeden tým = jeden vchod, jeden den.</p>
      </div>
      <div className="bg-white rounded-xl border p-5 flex gap-3 items-end">
        <div className="flex-1 space-y-2">
          <Label>Název týmu</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="např. Parta 1" data-testid="team-name" />
        </div>
        <Button onClick={add} style={{ background: "#0A2540" }} data-testid="team-add"><Plus className="h-4 w-4 mr-1" /> Přidat</Button>
      </div>
      <div className="bg-white rounded-xl border divide-y">
        {teams.map((t) => (
          <div key={t.id} className="flex items-center justify-between px-5 py-3">
            <span className="font-medium text-[#0A2540]">{t.name}</span>
            <span className={`text-xs px-2 py-0.5 rounded ${t.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100"}`}>{t.active ? "Aktivní" : "Neaktivní"}</span>
          </div>
        ))}
        {teams.length === 0 && <div className="px-5 py-6 text-slate-500">Zatím žádné týmy.</div>}
      </div>

      <Technicians teams={teams} />
    </div>
  );
}

function Technicians({ teams }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ email: "", name: "", password: "", team_id: "" });
  const load = useCallback(() => api.get("/technicians").then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e))), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (f.password.length < 8) return toast.error("Heslo musí mít alespoň 8 znaků");
    try { await api.post("/technicians", { ...f, team_id: f.team_id || null }); setF({ email: "", name: "", password: "", team_id: "" }); load(); toast.success("Technik vytvořen – může se přihlásit"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const patch = async (t, body, msg) => { try { await api.patch(`/technicians/${t.id}`, body); load(); toast.success(msg); } catch (e) { toast.error(apiErr(e)); } };
  const resetPw = (t) => { const p = window.prompt(`Nové heslo pro ${t.email} (min. 8 znaků):`); if (p) patch(t, { password: p }, "Heslo změněno"); };
  const del = async (t) => { if (!window.confirm(`Smazat technika ${t.email}?`)) return; try { await api.delete(`/technicians/${t.id}`); load(); toast.success("Technik smazán"); } catch (e) { toast.error(apiErr(e)); } };
  const teamName = (id) => teams.find((x) => x.id === id)?.name || "všechny týmy";
  return (
    <div className="space-y-4" data-testid="technicians-section">
      <div>
        <h2 className="text-xl font-bold text-[#0A2540] flex items-center gap-2"><Wrench className="h-5 w-5" /> Technici</h2>
        <p className="text-slate-500 text-sm mt-1">Technik se přihlásí na /prihlaseni a vidí harmonogram s telefony na konkrétní byty (jen svůj tým, nebo všechny). Může označit Hotovo / Nezastižen.</p>
      </div>
      <div className="bg-white rounded-xl border p-5 grid sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">E-mail (přihlašovací)</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="tech-email" /></div>
        <div className="space-y-1"><Label className="text-xs">Jméno</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="tech-name" /></div>
        <div className="space-y-1"><Label className="text-xs">Heslo (min. 8 znaků)</Label><Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="tech-password" /></div>
        <div className="space-y-1"><Label className="text-xs">Tým</Label>
          <Select value={f.team_id || "all"} onValueChange={(v) => setF({ ...f, team_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="tech-team"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Všechny týmy</SelectItem>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="sm:col-span-2"><Button onClick={add} disabled={!f.email || !f.password} style={{ background: "#0A2540" }} data-testid="tech-add"><Plus className="h-4 w-4 mr-1" /> Vytvořit přístup technika</Button></div>
      </div>
      <div className="bg-white rounded-xl border divide-y">
        {list.map((t) => (
          <div key={t.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`tech-row-${t.id}`}>
            <div className="flex-1 min-w-48"><div className="font-medium text-[#0A2540]">{t.name} <span className="text-slate-400 text-sm">· {t.email}</span></div><div className="text-xs text-slate-500">Tým: {teamName(t.team_id)}</div></div>
            <Select value={t.team_id || "all"} onValueChange={(v) => patch(t, { team_id: v === "all" ? "" : v }, "Tým změněn")}>
              <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Všechny týmy</SelectItem>{teams.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
            <Switch checked={t.active} onCheckedChange={(c) => patch(t, { active: c }, c ? "Aktivován" : "Deaktivován")} data-testid={`tech-active-${t.id}`} />
            <Button size="sm" variant="outline" onClick={() => resetPw(t)} data-testid={`tech-pw-${t.id}`}>Heslo</Button>
            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(t)} data-testid={`tech-del-${t.id}`}>Smazat</Button>
          </div>
        ))}
        {list.length === 0 && <div className="px-5 py-6 text-slate-500">Zatím žádný technik.</div>}
      </div>
    </div>
  );
}
